import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'
import AppShell from '../components/AppShell'
import { useAuditLog } from '../lib/queries'
import { SkeletonText } from '../components/ui/Skeleton'
import Button from '../components/ui/Button'
import type { AuditEntry } from '../types'

const ACTION_LABELS: Record<string, string> = {
  'channel.bind': 'Kanal bağlandı',
  'channel.unbind': 'Kanal bırakıldı',
  'channel.delete': 'Kanal silindi',
  'channel.owner_replaced': 'Kanal sahibi değişti',
  'channel.member.add': 'Mod eklendi',
  'channel.member.remove': 'Mod çıkarıldı',
  'overlay.create': 'Overlay oluşturuldu',
  'overlay.delete': 'Overlay silindi',
  'overlay.token_rotate': 'Overlay token yenilendi',
  'auth.password_change': 'Şifre değiştirildi',
  'auth.password_reset_requested': 'Şifre sıfırlama istendi',
  'auth.password_reset_completed': 'Şifre sıfırlandı',
  'auth.email_change_requested': 'Email değişimi istendi',
  'auth.email_change_completed': 'Email değiştirildi',
  'auth.email_change_cancelled': 'Email değişimi iptal edildi',
  'auth.login': 'Giriş yapıldı',
  'auth.register': 'Kayıt oldu',
}

export default function AuditLogPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = useAuditLog(id)
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set())

  const allActions = useMemo(
    () => Array.from(new Set((data || []).map((e) => e.action))).sort(),
    [data],
  )
  const visible = useMemo(
    () => (data || []).filter((e) => activeFilters.size === 0 || activeFilters.has(e.action)),
    [data, activeFilters],
  )

  return (
    <AppShell activeChannelId={id}>
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/channels/${id}/settings`)}>
            ← Ayarlar
          </Button>
          <h1 className="text-xl font-semibold">Audit log</h1>
        </div>

        {allActions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setActiveFilters(new Set())}
              className={`px-2.5 py-1 rounded-full text-xs border ${
                activeFilters.size === 0
                  ? 'bg-[var(--color-accent)] text-white border-transparent'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
              }`}
            >
              Hepsi
            </button>
            {allActions.map((a) => (
              <button
                key={a}
                onClick={() =>
                  setActiveFilters((prev) => {
                    const next = new Set(prev)
                    if (next.has(a)) next.delete(a)
                    else next.add(a)
                    return next
                  })
                }
                className={`px-2.5 py-1 rounded-full text-xs border ${
                  activeFilters.has(a)
                    ? 'bg-[var(--color-accent)] text-white border-transparent'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
                }`}
              >
                {ACTION_LABELS[a] || a}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <SkeletonText lines={8} />
        ) : visible.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Hiç kayıt yok.</p>
        ) : (
          <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-lg">
            {visible.map((e) => (
              <AuditRow key={e.id} entry={e} />
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  )
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const created = new Date(entry.created_at)
  const meta = entry.metadata
  return (
    <li className="p-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm">{ACTION_LABELS[entry.action] || entry.action}</div>
        <div className="text-xs text-[var(--color-text-muted)]">
          {entry.user_email && <span>@{entry.user_email}</span>}
          {meta && Object.keys(meta).length > 0 && (
            <span className="ml-2">— {summarizeMeta(meta)}</span>
          )}
        </div>
      </div>
      <time
        className="text-xs text-[var(--color-text-muted)] shrink-0"
        title={format(created, 'PPpp', { locale: tr })}
      >
        {formatDistanceToNow(created, { locale: tr, addSuffix: true })}
      </time>
    </li>
  )
}

function summarizeMeta(m: Record<string, unknown>): string {
  // Yaygın alanları öne çıkar
  if (m.old_email && m.new_email) {
    return `${m.old_email} → ${m.new_email}`
  }
  if (m.email) return String(m.email)
  if (m.slug) return String(m.slug)
  return JSON.stringify(m)
}
